import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Page, PageHeader, Section, List, Row, Empty, TextLink, Loading, humanize } from "@/components/calm/Calm";
import { propertyCopy } from "@/lib/clientTypes";

// Billing for property accounts: each property's plan (billed per property
// through Stripe, managed in the Stripe portal) plus the prices this account
// type pays. Prices come from GET /api/dev/catalog, so anything not yet
// approved reads "Priced at booking" here too.
const cad = (cents) => `$${(Number(cents) / 100).toLocaleString("en-CA", { maximumFractionDigits: 0 })}`;

export default function DeveloperBillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const copy = propertyCopy(user?.account_type);
  const [projects, setProjects] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/dev/projects")
      .then((r) => { if (live) setProjects(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (live) { setProjects([]); setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t load billing."); } });
    api.get("/dev/catalog", { params: { type: user?.account_type || "builder" } })
      .then((r) => { if (live) setCatalog(r.data && Array.isArray(r.data.families) ? r.data : { families: [] }); })
      .catch(() => { if (live) setCatalog({ families: [] }); });
    return () => { live = false; };
  }, [user?.account_type]);

  const planName = (key) => {
    for (const f of catalog?.families || []) {
      const hit = f.items.find((x) => x.key === key);
      if (hit) return hit.name;
    }
    return key ? humanize(key) : null;
  };

  async function manage(id) {
    setOpening(id);
    setError("");
    try {
      const res = await api.post(`/dev/projects/${id}/subscription/disable`, {});
      window.location.href = res.data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t open billing.");
      setOpening(null);
    }
  }

  const billed = (projects || []).filter((p) => p.subscription_status && p.subscription_status !== "inactive");

  return (
    <Page testid="developer-billing" width={760}>
      <PageHeader title="Billing" help="plans-billing" subtitle={`Plans are billed per ${copy.thing}. Single visits are paid when you book.`} />
      {error && <div role="alert" style={{ fontSize: 13, color: "var(--c-attn)" }}>{error}</div>}

      <Section title="Plans">
        {projects === null ? <Loading /> : !billed.length ? (
          <Empty action={<TextLink onClick={() => navigate("/developer/projects")}>{`Open ${copy.things.toLowerCase()}`}</TextLink>}>
            {`No ${copy.thing} is on a plan. Pick one from any ${copy.thing}.`}
          </Empty>
        ) : (
          <List testid="developer-billing-plans">
            {billed.map((p) => (
              <Row key={p.id} wide={false} title={p.name}
                meta={[planName(p.subscription_tier)].filter(Boolean)}
                status={humanize(p.subscription_status)}
                attention={p.subscription_status === "past_due"}
                aside={<TextLink hit onClick={() => manage(p.id)}>{opening === p.id ? "Opening…" : "Manage"}</TextLink>} />
            ))}
          </List>
        )}
      </Section>

      <Section title="Prices" note="In Canadian dollars, before tax.">
        {catalog === null ? <Loading /> : !catalog.families.length ? <Empty>Prices are shown when you book.</Empty> : (
          <div data-testid="developer-billing-prices" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {catalog.families.map((f) => (
              <div key={f.key}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{f.title}</div>
                <List>
                  {f.items.map((x) => (
                    <Row key={x.key} wide={false} title={x.name}
                      status={x.price_cad_cents == null ? (x.price_label || "Quoted") : `${cad(x.price_cad_cents)}${x.kind === "plan" ? " /mo" : ""}${x.interval_count > 1 && x.billing_label ? `, ${x.billing_label.charAt(0).toLowerCase()}${x.billing_label.slice(1)}` : ""}`} />
                  ))}
                </List>
              </div>
            ))}
          </div>
        )}
      </Section>
    </Page>
  );
}
